<script lang="ts">
	// UC-13. The tills, grouped by the shop they sell in: whether each is
	// paired and whether it has a cash drawer, what it last reported (online
	// or offline, last seen on the restaurant's clock, app build, waiting
	// sales and platform, or Never seen), and revoking a till.
	//
	// Adding a till happens in a Sheet, which then shows the pairing code
	// once, with the time it has left. The server keeps the code only as a
	// hash, so closing the sheet is the last anyone sees of it.

	import Band from '../components/Band.svelte';
	import Button from '../components/Button.svelte';
	import Card from '../components/Card.svelte';
	import Chip from '../components/Chip.svelte';
	import Icon from '../components/Icon.svelte';
	import Field from '../components/Field.svelte';
	import Loading from '../components/Loading.svelte';
	import Secret from '../components/Secret.svelte';
	import Segmented from '../components/Segmented.svelte';
	import Sheet from '../components/Sheet.svelte';
	import { untrack } from 'svelte';
	import { api } from '../api/client';
	import { fill, message, type Language } from '../i18n/messages';
	import { formatWait } from '../till/passcode.svelte';
	import {
		loadTills,
		ownerShops,
		ownerTills,
		tillRefusals,
		setTillRefusal,
		clearTillRefusal,
		type Till,
	} from '../owner/data.svelte';
	import { pendingUndo, removeWithUndo } from '../owner/notices.svelte';
	import { cashPointRefusal, refusalWords, tillReport } from '../owner/tills';

	interface Props {
		language: Language;
		/** Bumped by the title row's "New till". */
		actionAsked: number;
		shopId?: string;
		/** The restaurant's time zone: last seen is on its clock, never the browser's. */
		timeZone: string;
	}

	let { language, actionAsked, shopId = '', timeZone }: Props = $props();

	const shops = $derived(ownerShops());
	const tills = $derived(ownerTills());
	const noShops = $derived(shops.loaded && shops.value.length === 0);

	// What the sheet is doing: a form, then the pairing code it produced.
	type SheetState =
		| { kind: 'form'; name: string; shopId: string; drawer: 'yes' | 'no' }
		| { kind: 'code'; name: string; code: string; until: number };
	let sheet = $state<SheetState | null>(null);
	let open = $state(false);
	let adding = $state(false);
	let failure = $state('');

	// A clock for the pairing code's countdown.
	let now = $state(Date.now());
	$effect(() => {
		const tick = setInterval(() => (now = Date.now()), 1000);
		return () => clearInterval(tick);
	});

	// What each till last reported goes stale on a page left open: read the
	// tills again every 30 seconds, so the online mark follows the server's
	// TillOfflineAfter (internal/core/domain/heartbeat.go). Never while a revoke waits for its Undo,
	// which would put the revoked till back on the list.
	// The dashboard read the tills when it started, maybe long ago, so the
	// page reads them once more as it opens, then on every tick.
	let reportedAt = $state(Date.now());
	function refresh() {
		if (pendingUndo() !== null) return;
		void loadTills(message(language, 'loadFailed')).then(() => (reportedAt = Date.now()));
	}
	$effect(() => {
		untrack(refresh);
		const tick = setInterval(refresh, 30_000);
		return () => clearInterval(tick);
	});

	$effect(() => {
		if (actionAsked > 0 && !noShops) startAdd();
	});

	function startAdd() {
		sheet = { kind: 'form', name: '', shopId: shopId || shops.value[0]?.id || '', drawer: 'yes' };
		failure = '';
		open = true;
	}

	async function add() {
		if (sheet?.kind !== 'form' || !sheet.name.trim() || !sheet.shopId || adding) return;
		adding = true;
		failure = '';
		const name = sheet.name.trim();
		try {
			const { data, error } = await api.POST('/api/owner/tills', {
				body: { name, shopId: sheet.shopId, hasDrawer: sheet.drawer === 'yes' },
			});
			if (error !== undefined || !data) {
				failure = error?.message ?? message(language, 'loadFailed');
				return;
			}
			now = Date.now();
			sheet = {
				kind: 'code',
				name,
				code: data.pairingCode,
				until: Date.now() + data.expiresInSeconds * 1000,
			};
			await loadTills(message(language, 'loadFailed'));
		} catch {
			failure = message(language, 'loadFailed');
		} finally {
			adding = false;
		}
	}

	function revoke(till: Till, shopName: string) {
		clearTillRefusal(till.id);
		const at = tills.value.indexOf(till);
		tills.value = tills.value.filter((t) => t.id !== till.id);
		removeWithUndo(
			`${message(language, 'tillsRevoked')} ${till.name} · ${shopName}. ${message(language, 'tillsStopped')}`,
			async () => {
				try {
					const { error } = await api.POST('/api/owner/tills/{tillId}/revoke', {
						params: { path: { tillId: till.id } },
					});
					const refusal = cashPointRefusal(error);
					if (refusal) setTillRefusal(till.id, refusal);
				} finally {
					await loadTills(message(language, 'loadFailed'));
				}
			},
			() => {
				tills.value.splice(at, 0, till);
			},
		);
	}

	const groups = $derived(
		shops.value
			.filter((shop) => !shopId || shop.id === shopId)
			.map((shop) => ({ shop, tills: tills.value.filter((t) => t.shopId === shop.id) }))
			.filter((group) => group.tills.length > 0),
	);

	const secondsLeft = $derived(
		sheet?.kind === 'code' ? Math.max(0, Math.ceil((sheet.until - now) / 1000)) : 0,
	);

	const sheetTitle = $derived(
		sheet?.kind === 'form' ? message(language, 'tillsAdd') : (sheet?.name ?? ''),
	);
</script>

<div class="tills" data-testid="panel-tills">
	{#if !tills.loaded || !shops.loaded}
		<div class="wait">
			<Loading
				size="lg"
				label={message(language, 'loading')}
				caption={message(language, 'loading')}
			/>
		</div>
	{:else}
		<!-- A failed add speaks in its sheet; this band is the list's own read. -->
		{#if tills.failure}
			<Band tone="flag" testId="tills-error">{tills.failure}</Band>
		{/if}
		{#if noShops}
			<Card pad>
				<p class="empty" data-testid="tills-no-shops">
					{message(language, 'tillsNoShops')}
				</p>
			</Card>
		{:else if groups.length === 0}
			<Card pad>
				<p class="empty" data-testid="tills-empty">{message(language, 'tillsEmpty')}</p>
			</Card>
		{/if}
		{#each groups as group (group.shop.id)}
			<Card
				title={group.shop.name}
				note="{group.tills.length} {message(language, 'tillsCount')}"
				testId="tills-shop-{group.shop.id}"
			>
				{#each group.tills as till (till.id)}
					{@const report = tillReport(language, till, timeZone, new Date(reportedAt))}
					<div class="till" data-testid="till-{till.id}">
						<div class="who">
							<span class="name">{till.name}</span>
							<div class="facts">
								<Chip
									text={till.paired
										? message(language, 'tillsPaired')
										: message(language, 'tillsUnpaired')}
									tone={till.paired ? 'ok' : 'flag'}
									size="sm"
									dot
									testId={till.paired ? `till-paired-${till.id}` : `till-unpaired-${till.id}`}
								/>
								<!-- What the till has, not a state: the glyph and plain words. -->
								<span class="drawer" data-testid="till-drawer-{till.id}">
									<Icon name="drawer" size={14} />
									{till.hasDrawer
										? message(language, 'tillsHasDrawer')
										: message(language, 'tillsNoDrawer')}
								</span>
								<!-- Where its cash goes (UC-61); a manager on the till chooses it. -->
								<span class="cash-point" data-testid="till-cash-point-{till.id}"
									>{till.cashPointTillId
										? fill(
												message(language, 'tillsCashPointOther'),
												tills.value.find((other) => other.id === till.cashPointTillId)?.name ||
													till.cashPointTillId,
											)
										: message(language, 'tillsCashPointSelf')}</span
								>
							</div>
							<!-- What its last heartbeat said (#978); the screen builds are not shown. -->
							<div class="facts" data-testid="till-report-{till.id}">
								{#if report.seen}
									<Chip
										text={report.chip}
										tone={report.online ? 'ok' : 'neutral'}
										size="sm"
										dot
										testId={report.online ? `till-online-${till.id}` : `till-offline-${till.id}`}
									/>
									{#each report.facts as fact (fact)}
										<span class="report">{fact}</span>
									{/each}
								{:else}
									<span class="report" data-testid="till-never-seen-{till.id}">{report.text}</span>
								{/if}
							</div>
						</div>
						<div class="actions">
							<Button
								testId="till-revoke-{till.id}"
								label={message(language, 'tillsRevoke')}
								tone="text-quiet"
								size="sm"
								onPress={() => revoke(till, group.shop.name)}
							/>
						</div>
					</div>
					{#if tillRefusals()[till.id]}
						<Band tone="flag" testId="till-refused-{till.id}"
							>{refusalWords(language, tillRefusals()[till.id]!)}</Band
						>
					{/if}
				{/each}
			</Card>
		{/each}
	{/if}
</div>

<Sheet
	{open}
	title={sheetTitle}
	primaryLabel={sheet?.kind === 'form'
		? message(language, 'tillsSave')
		: message(language, 'tillsCodeDone')}
	primaryTestId={sheet?.kind === 'form' ? 'till-save' : 'secret-done'}
	primaryDisabled={sheet?.kind === 'form' && (!sheet.name.trim() || !sheet.shopId)}
	primaryState={adding ? 'pending' : 'idle'}
	pendingLabel={message(language, 'tillsAdding')}
	onPrimary={() => (sheet?.kind === 'form' ? void add() : (open = false))}
	onClose={() => (open = false)}
	closeLabel={message(language, 'sheetClose')}
	closeTestId="till-close"
	testId="till-form"
>
	<div class="sheet" data-scroll="">
		{#if sheet?.kind === 'form'}
			{#if failure}
				<Band tone="flag">{failure}</Band>
			{/if}
			<div class="fields">
				<Field
					testId="till-name"
					label={message(language, 'tillsName')}
					placeholder={message(language, 'tillsNamePlaceholder')}
					value={sheet.name}
					maxLength={120}
					onInput={(v) => sheet?.kind === 'form' && (sheet.name = v)}
				/>
				<div class="group">
					<span class="label">{message(language, 'tillsShop')}</span>
					<div class="picks">
						{#each shops.value.filter((shop) => !shopId || shop.id === shopId) as shop (shop.id)}
							<Button
								testId="till-shop-{shop.id}"
								label={shop.name}
								tone={sheet.shopId === shop.id ? 'ink' : 'secondary'}
								size="sm"
								onPress={() => sheet?.kind === 'form' && (sheet.shopId = shop.id)}
							/>
						{/each}
					</div>
				</div>
				<div class="group">
					<span class="label">{message(language, 'tillsDrawerQ')}</span>
					<Segmented
						label={message(language, 'tillsDrawer')}
						options={[
							{ value: 'yes', label: message(language, 'yes'), testId: 'till-drawer-yes' },
							{ value: 'no', label: message(language, 'no'), testId: 'till-drawer-no' },
						]}
						value={sheet.drawer}
						onChange={(picked) => sheet?.kind === 'form' && (sheet.drawer = picked as 'yes' | 'no')}
					/>
				</div>
			</div>
		{:else if sheet?.kind === 'code'}
			<Secret
				testId="pairing-code-box"
				label="{message(language, 'tillsCodeTitle')} {sheet.name}"
				value={sheet.code}
				valueTestId="pairing-code"
				note={secondsLeft > 0
					? `${message(language, 'tillsCodeExpires')} ${formatWait(secondsLeft)}. ${message(language, 'tillsCodeOnce')}`
					: message(language, 'tillsCodeExpired')}
			/>
		{/if}
	</div>
</Sheet>

<style>
	.tills {
		display: flex;
		flex-direction: column;
		gap: var(--grid-gap);
	}

	.wait {
		display: grid;
		place-items: center;
		min-block-size: 240px;
	}

	.empty {
		margin: 0;
		font-size: var(--text-md);
		color: var(--text-body);
	}

	.till {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: var(--space-4) var(--space-7);
		padding: var(--row-pad);
		border-block-end: var(--border-width) solid var(--border-inset);
	}

	.who {
		flex: 1 1 280px;
		min-inline-size: 0;
		display: flex;
		flex-direction: column;
		gap: var(--space-2);
	}

	.name {
		font-size: var(--text-sm);
		font-weight: 600;
	}

	.facts {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: var(--space-1) var(--space-4);
		font-size: var(--text-micro);
	}

	.drawer {
		display: inline-flex;
		align-items: center;
		gap: var(--space-2);
		color: var(--text-muted);
		white-space: nowrap;
	}

	.cash-point,
	.report {
		color: var(--text-muted);
		white-space: nowrap;
	}

	.actions {
		display: flex;
		flex-wrap: wrap;
		gap: var(--space-3);
		align-items: center;
	}

	.sheet {
		flex: 1;
		min-block-size: 0;
		overflow-y: auto;
		overflow-x: hidden;
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
	}

	.fields {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(min(100%, 240px), 1fr));
		gap: var(--space-7);
	}

	.group {
		display: flex;
		flex-direction: column;
		gap: var(--space-2);
		min-inline-size: 0;
	}

	.label {
		font-size: var(--text-2xs);
		color: var(--text-muted);
	}

	.picks {
		display: flex;
		flex-wrap: wrap;
		gap: var(--space-2);
	}
</style>
